import { useEffect, useRef, useState } from 'react';
import { localTimeToUtc } from '@/shared/lib/eventTime';
import type { RecurrenceType } from '@/entities/event/model/event.types';
import {
  Autocomplete as MuiAutocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Skeleton,
  Stack,
  SvgIcon,
  TextField,
  Typography,
  alpha,
  useTheme,
} from '@mui/material';
import type { SvgIconProps } from '@mui/material/SvgIcon';
import { useNavigate } from 'react-router-dom';
import { DjPageShell } from '@/shared/ui/DjPageShell';
import { MKDate } from '@/shared/ui/MKDate';
import { useAuth } from '@/shared/auth/useAuth';
import { eventApi } from '@/entities/event/api/event.api';
import { spotifyApi } from '@/entities/spotify/api/spotify.api';
import type { SpotifyPlaylist } from '@/entities/spotify/model/spotify.types';
import type { EventLocation } from '@/entities/event/model/event.types';
import { routePaths } from '@/app/router/routePaths';
import { env } from '@/shared/config/env';
import { loadGoogleMapsScript } from '@/shared/lib/googleMaps';

const iconPaths = {
  back: 'M20 11H7.83l5.59-5.59L12 4l-8 8 8 8 1.41-1.41L7.83 13H20v-2z',
  arrow: 'M12 4l-1.41 1.41L15.17 10H4v2h11.17l-4.58 4.59L12 18l7-7-7-7z',
  calendar: 'M19 3h-1V1h-2v2H8V1H6v2H5c-1.11 0-1.99.9-1.99 2L3 19c0 1.1.89 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm0 16H5V8h14v11zM7 10h5v5H7z',
  location: 'M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z',
  notes: 'M3 18h12v-2H3v2zm0-5h12v-2H3v2zm0-7v2h12V6H3zm14 9h2v-3h3v-2h-3v-3h-2v3h-3v2h3v3z',
  repeat: 'M7 7h10v3l4-4-4-4v3H5v6h2V7zm10 10H7v-3l-4 4 4 4v-3h12v-6h-2v4z',
  check: 'M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z',
  spotify: 'M12 2C6.477 2 2 6.477 2 12s4.477 10 10 10 10-4.477 10-10S17.523 2 12 2zm4.586 14.424a.623.623 0 0 1-.857.207c-2.348-1.435-5.304-1.76-8.785-.964a.623.623 0 1 1-.277-1.215c3.809-.87 7.077-.496 9.712 1.115a.623.623 0 0 1 .207.857zm1.223-2.722a.78.78 0 0 1-1.072.257c-2.687-1.652-6.785-2.131-9.965-1.166a.78.78 0 0 1-.973-.519.78.78 0 0 1 .518-.973c3.632-1.102 8.147-.568 11.234 1.329a.78.78 0 0 1 .258 1.072zm.105-2.835C14.692 8.95 9.375 8.775 6.297 9.71a.937.937 0 1 1-.543-1.795c3.532-1.07 9.404-.862 13.115 1.338a.936.936 0 0 1 .045 1.614z',
};

const PLAYLIST_GRADIENTS = [
  'linear-gradient(135deg,#5B21B6,#DB2777)',
  'linear-gradient(135deg,#DB2777,#D97706)',
  'linear-gradient(135deg,#059669,#5B21B6)',
  'linear-gradient(135deg,#1DB954,#059669)',
  'linear-gradient(135deg,#D97706,#DC2626)',
  'linear-gradient(135deg,#7C3AED,#1DB954)',
];
const SPOTIFY_GREEN = '#1DB954';

function AppIcon({ path, ...props }: SvgIconProps & { path: string }) {
  return <SvgIcon {...props}><path d={path} /></SvgIcon>;
}

function SectionLabel({ icon, children }: { icon: string; children: string }) {
  const theme = useTheme();
  return (
    <Stack direction="row" alignItems="center" spacing={1}>
      <AppIcon path={icon} sx={{ fontSize: 16, color: alpha(theme.palette.common.white, 0.3) }} />
      <Typography sx={{ fontSize: 11, fontWeight: 800, letterSpacing: '0.06em', textTransform: 'uppercase', color: alpha(theme.palette.common.white, 0.4) }}>
        {children}
      </Typography>
    </Stack>
  );
}

type PlaceOption = {
  label: string;
  placeId: string;
  secondaryText: string;
};

function VenueAutocomplete({
  venueName,
  location,
  onVenueChange,
  fieldSx,
  error,
}: {
  venueName: string;
  location: EventLocation | undefined;
  onVenueChange: (name: string, loc?: EventLocation) => void;
  fieldSx: object;
  error?: string;
}) {
  const theme = useTheme();
  const [options, setOptions] = useState<PlaceOption[]>([]);
  const [fetching, setFetching] = useState(false);
  const [mapsReady, setMapsReady] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout>>();
  const dummyRef = useRef<HTMLDivElement>(null);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const acServiceRef = useRef<any>(null);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const placesServiceRef = useRef<any>(null);

  useEffect(() => {
    if (!env.googleMapsApiKey) return;
    loadGoogleMapsScript(env.googleMapsApiKey)
      .then(() => {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const g = (window as any).google;
        acServiceRef.current = new g.maps.places.AutocompleteService();
        setMapsReady(true);
      })
      .catch(() => {});
  }, []);

  const fetchPredictions = (input: string) => {
    if (!mapsReady || input.trim().length < 2) { setOptions([]); return; }
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setFetching(true);

      // Safety: if the callback never fires (key domain restriction, quota, etc.)
      // stop the spinner after 5 s so it doesn't hang forever.
      const safetyTimer = setTimeout(() => setFetching(false), 5000);

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      acServiceRef.current?.getPlacePredictions({ input }, (predictions: any, status: string) => {
        clearTimeout(safetyTimer);
        setFetching(false);
        if (status === 'OK' && predictions) {
          setOptions(
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            predictions.slice(0, 5).map((p: any) => ({
              label: p.structured_formatting?.main_text ?? p.description,
              placeId: p.place_id as string,
              secondaryText: p.structured_formatting?.secondary_text ?? '',
            }))
          );
        } else {
          if (status !== 'ZERO_RESULTS') console.warn('[Places]', status);
          setOptions([]);
        }
      });
    }, 280);
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handleSelect = (_: any, selected: PlaceOption | string | null) => {
    if (!selected || typeof selected === 'string') {
      onVenueChange((selected as string | null) ?? '');
      return;
    }
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const g = (window as any).google;
    if (!placesServiceRef.current && dummyRef.current) {
      placesServiceRef.current = new g.maps.places.PlacesService(dummyRef.current);
    }
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    placesServiceRef.current?.getDetails({ placeId: selected.placeId, fields: ['name', 'formatted_address', 'geometry', 'place_id'] }, (place: any, status: string) => {
      if (status === 'OK' && place) {
        onVenueChange(place.name ?? selected.label, {
          placeId: place.place_id,
          lat: place.geometry?.location?.lat() ?? 0,
          lng: place.geometry?.location?.lng() ?? 0,
          address: place.formatted_address,
        });
      } else {
        onVenueChange(selected.label);
      }
      setOptions([]);
    });
  };

  return (
    <>
      <div ref={dummyRef} style={{ display: 'none' }} />
      <Stack spacing={0.75}>
        <MuiAutocomplete
          freeSolo
          options={options}
          loading={fetching}
          inputValue={venueName}
          filterOptions={(x) => x}
          onInputChange={(_, newInput, reason) => {
            if (reason === 'input') {
              onVenueChange(newInput, undefined);
              fetchPredictions(newInput);
            }
          }}
          onChange={handleSelect}
          getOptionLabel={(opt) => (typeof opt === 'string' ? opt : opt.label)}
          renderOption={(props, opt) => {
            const { key, ...rest } = props as { key: string } & React.HTMLAttributes<HTMLLIElement>;
            return (
              <Box component="li" key={key} {...rest} sx={{ px: 2, py: 1.25 }}>
                <Stack spacing={0.2}>
                  <Typography sx={{ fontSize: 14, fontWeight: 700, color: 'common.white', lineHeight: 1.2 }}>
                    {typeof opt === 'string' ? opt : opt.label}
                  </Typography>
                  <Typography sx={{ fontSize: 12, color: alpha(theme.palette.common.white, 0.45), lineHeight: 1.2 }}>
                    {typeof opt === 'string' ? '' : opt.secondaryText}
                  </Typography>
                </Stack>
              </Box>
            );
          }}
          slotProps={{
            paper: {
              sx: {
                bgcolor: '#14213a',
                border: `1px solid ${alpha(theme.palette.common.white, 0.14)}`,
                borderRadius: 1.5,
                boxShadow: `0 16px 40px ${alpha(theme.palette.common.black, 0.5)}`,
                mt: 0.5,
                '& .MuiAutocomplete-option': {
                  borderRadius: 1,
                  mx: 0.5,
                  '&[aria-selected="true"], &.Mui-focused': {
                    bgcolor: `${alpha(theme.palette.common.white, 0.08)} !important`,
                  },
                },
                '& .MuiAutocomplete-noOptions': {
                  color: alpha(theme.palette.common.white, 0.4),
                  fontSize: 13,
                },
                '& .MuiAutocomplete-loading': {
                  color: alpha(theme.palette.common.white, 0.4),
                  fontSize: 13,
                },
              },
            },
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Venue"
              placeholder="Search for a venue…"
              sx={fieldSx}
              InputProps={{
                ...params.InputProps,
                startAdornment: (
                  <>
                    <AppIcon path={iconPaths.location} sx={{ fontSize: 18, color: alpha(theme.palette.common.white, 0.3), ml: 0.5, mr: 0.75, flexShrink: 0 }} />
                    {params.InputProps.startAdornment}
                  </>
                ),
                endAdornment: (
                  <>
                    {fetching && <CircularProgress size={16} sx={{ color: alpha(theme.palette.common.white, 0.4) }} />}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          )}
        />

        {location?.address && (
          <Stack direction="row" alignItems="center" spacing={0.75} sx={{ px: 0.5 }}>
            <AppIcon path={iconPaths.check} sx={{ fontSize: 13, color: '#10B981' }} />
            <Typography sx={{ fontSize: 12, color: alpha(theme.palette.common.white, 0.45), lineHeight: 1.3 }}>
              {location.address}
            </Typography>
          </Stack>
        )}
        {error && (
          <Typography sx={{ fontSize: 12, color: 'error.light', px: 0.5 }}>{error}</Typography>
        )}
      </Stack>
    </>
  );
}

const WEEK_DAYS: { label: string; short: string; dow: number }[] = [
  { label: 'Monday',    short: 'Mon', dow: 1 },
  { label: 'Tuesday',   short: 'Tue', dow: 2 },
  { label: 'Wednesday', short: 'Wed', dow: 3 },
  { label: 'Thursday',  short: 'Thu', dow: 4 },
  { label: 'Friday',    short: 'Fri', dow: 5 },
  { label: 'Saturday',  short: 'Sat', dow: 6 },
  { label: 'Sunday',    short: 'Sun', dow: 0 },
];

const recurrenceOptions: { value: RecurrenceType; title: string; copy: string; icon: string }[] = [
  {
    value: 'once',
    title: 'Once-off',
    copy: 'A single event on one date.',
    icon: iconPaths.calendar,
  },
  {
    value: 'weekly',
    title: 'Weekly',
    copy: 'Repeats on selected days.',
    icon: iconPaths.repeat,
  },
];

type FormState = {
  name: string;
  description: string;
  venueName: string;
  location: EventLocation | undefined;
  recurrence: RecurrenceType;
  recurrenceDays: number[];
  recurrenceEndDate: string;
  eventDate: string;
  startTime: string;
  endTime: string;
  spotifyPlaylistId: string | undefined;
  spotifyPlaylistName: string | undefined;
  sharePlaylist: boolean;
};

const initialForm: FormState = {
  name: '',
  description: '',
  venueName: '',
  location: undefined,
  recurrence: 'once',
  recurrenceDays: [],
  recurrenceEndDate: '',
  eventDate: '',
  startTime: '',
  endTime: '',
  spotifyPlaylistId: undefined,
  spotifyPlaylistName: undefined,
  sharePlaylist: false,
};

export function DjCreateEventPage() {
  const theme = useTheme();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [form, setForm] = useState<FormState>(initialForm);
  const [errors, setErrors] = useState<Partial<Record<keyof FormState, string>>>({});
  const [submitting, setSubmitting] = useState(false);
  const [playlists, setPlaylists] = useState<SpotifyPlaylist[]>([]);
  const [loadingPlaylists, setLoadingPlaylists] = useState(false);

  useEffect(() => {
    if (!user?.spotifyConnected) return;
    setLoadingPlaylists(true);
    spotifyApi.getPlaylists()
      .then(setPlaylists)
      .finally(() => setLoadingPlaylists(false));
  }, [user?.spotifyConnected]);

  const today = new Date().toISOString().slice(0, 10);

  const set = (field: keyof FormState) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setForm((prev) => ({ ...prev, [field]: e.target.value }));
    setErrors((prev) => ({ ...prev, [field]: undefined }));
  };

  const toggleDay = (dow: number) => {
    setForm((prev) => ({
      ...prev,
      recurrenceDays: prev.recurrenceDays.includes(dow)
        ? prev.recurrenceDays.filter((d) => d !== dow)
        : [...prev.recurrenceDays, dow],
    }));
    setErrors((prev) => ({ ...prev, recurrenceDays: undefined }));
  };

  const selectedDayText = form.recurrenceDays.length === 0
    ? 'Choose the days this event repeats'
    : `${form.recurrenceDays.length} day${form.recurrenceDays.length === 1 ? '' : 's'} selected`;

  const validate = (): boolean => {
    const next: Partial<Record<keyof FormState | 'recurrenceDays', string>> = {};
    if (!form.name.trim())       next.name = 'Event name is required';
    if (!form.venueName.trim())  next.venueName = 'Venue is required';
    if (!form.location)          next.venueName = next.venueName || 'Select a venue from the suggestions to capture the location';
    if (form.recurrence === 'once' && !form.eventDate) next.eventDate = 'Date is required';
    if (form.recurrence === 'once' && form.eventDate && form.eventDate < today) next.eventDate = 'Event date must be in the future';
    if (form.recurrenceEndDate && form.recurrenceEndDate < today) next.recurrenceEndDate = 'End date must be in the future';
    if (form.recurrence === 'weekly' && form.recurrenceDays.length === 0) next.recurrenceDays = 'Select at least one day';
    if (!form.startTime)         next.startTime = 'Start time is required';
    if (!form.endTime)           next.endTime = 'End time is required';
    if (form.startTime && form.endTime && form.endTime === form.startTime) next.endTime = 'End time must be different from start time';
    if (!form.spotifyPlaylistId) next.spotifyPlaylistId = 'Select a Spotify playlist';
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) return;
    setSubmitting(true);
    try {
      // Use today as reference date for UTC conversion when no specific date
      const refDate = form.eventDate || new Date().toISOString().slice(0, 10);
      const created = await eventApi.createEvent({
        name: form.name.trim(),
        description: form.description.trim() || undefined,
        venueName: form.venueName.trim() || undefined,
        location: form.location,
        recurrence: form.recurrence,
        recurrenceDays: form.recurrence === 'weekly' ? form.recurrenceDays : undefined,
        recurrenceEndDate: form.recurrence === 'weekly' && form.recurrenceEndDate ? form.recurrenceEndDate : undefined,
        eventDate: form.recurrence === 'once' ? form.eventDate || undefined : undefined,
        startTime: form.startTime ? localTimeToUtc(refDate, form.startTime) : undefined,
        endTime: form.endTime ? localTimeToUtc(refDate, form.endTime) : undefined,
        spotifyPlaylistId: form.spotifyPlaylistId,
        spotifyPlaylistName: form.spotifyPlaylistName,
        sharePlaylist: form.sharePlaylist,
      });
      navigate(routePaths.djEventDetail.replace(':eventId', created.eventId));
    } finally {
      setSubmitting(false);
    }
  };

  const fieldSx = {
    '& .MuiOutlinedInput-root': {
      color: 'common.white',
      bgcolor: alpha(theme.palette.common.white, 0.08),
      borderRadius: 1.5,
      minHeight: 48,
      '& fieldset': { borderColor: alpha(theme.palette.common.white, 0.16) },
      '&:hover fieldset': { borderColor: alpha(theme.palette.common.white, 0.32) },
      '&.Mui-focused fieldset': { borderColor: theme.palette.secondary.main },
    },
    '& .MuiInputLabel-root': { color: alpha(theme.palette.common.white, 0.45) },
    '& .MuiInputLabel-root.Mui-focused': { color: theme.palette.secondary.light },
    '& .MuiFormHelperText-root': { color: theme.palette.error.light },
    '& input[type="date"]::-webkit-calendar-picker-indicator': { filter: 'invert(1) opacity(0.45)', cursor: 'pointer' },
    '& input[type="time"]::-webkit-calendar-picker-indicator': { filter: 'invert(1) opacity(0.45)', cursor: 'pointer' },
    '& input::placeholder': { color: alpha(theme.palette.common.white, 0.3), opacity: 1 },
    '& textarea::placeholder': { color: alpha(theme.palette.common.white, 0.3), opacity: 1 },
    '& .MuiAutocomplete-clearIndicator': { color: alpha(theme.palette.common.white, 0.4) },
    '& .MuiAutocomplete-popupIndicator': { color: alpha(theme.palette.common.white, 0.4) },
  };

  return (
    <DjPageShell maxWidth="sm">
      <Stack spacing={3}>

        {/* Breadcrumb */}
        <Stack direction="row" alignItems="center" spacing={1.5}>
          <Button
            onClick={() => navigate(routePaths.djEvents)}
            startIcon={<AppIcon path={iconPaths.back} />}
            size="small"
            sx={{
              color: alpha(theme.palette.common.white, 0.5),
              fontSize: 13,
              fontWeight: 700,
              '&:hover': { color: 'common.white', bgcolor: alpha(theme.palette.common.white, 0.08) },
            }}
          >
            Events
          </Button>
          <Typography sx={{ color: alpha(theme.palette.common.white, 0.22), fontSize: 14 }}>/</Typography>
          <Typography sx={{ color: alpha(theme.palette.common.white, 0.55), fontSize: 13, fontWeight: 700 }}>
            New Event
          </Typography>
        </Stack>

        {/* Page title */}
        <Box
          sx={{
            position: 'relative',
            overflow: 'hidden',
            p: { xs: 2, sm: 2.5 },
            borderRadius: 2.25,
            border: `1px solid ${alpha(theme.palette.common.white, 0.12)}`,
            bgcolor: alpha(theme.palette.common.white, 0.055),
            backdropFilter: 'blur(20px)',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: -80,
              right: -130,
              width: 300,
              height: 200,
              pointerEvents: 'none',
              background: `linear-gradient(115deg, transparent 0 24%, ${alpha(theme.palette.secondary.light, 0.2)} 24% 36%, transparent 36% 52%, ${alpha(theme.palette.warning.light, 0.14)} 52% 62%, transparent 62%)`,
              transform: 'skewY(-8deg)',
            },
          }}
        >
          <Typography variant="h4" sx={{ position: 'relative', fontSize: { xs: 30, sm: 38 }, fontWeight: 900, color: 'common.white', lineHeight: 1.05 }}>
            New Event
          </Typography>
          <Typography sx={{ position: 'relative', mt: 0.5, fontSize: 15, color: alpha(theme.palette.common.white, 0.58) }}>
            Set up your set — the crowd gets a QR link once it's live.
          </Typography>
        </Box>

        {/* Form card */}
        <Box
          sx={{
            position: 'relative',
            overflow: 'hidden',
            p: { xs: 2.5, sm: 3 },
            borderRadius: 2,
            border: `1px solid ${alpha(theme.palette.common.white, 0.12)}`,
            bgcolor: alpha(theme.palette.common.white, 0.065),
            backdropFilter: 'blur(20px)',
            boxShadow: `0 20px 46px ${alpha(theme.palette.common.black, 0.16)}`,
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              height: 3,
              background: `linear-gradient(90deg, ${theme.palette.warning.light}, ${theme.palette.secondary.light}, ${theme.palette.primary.light})`,
              opacity: 0.8,
            },
          }}
        >
          <Stack spacing={3}>

            {/* Details */}
            <Stack spacing={2}>
              <SectionLabel icon={iconPaths.notes}>Details</SectionLabel>

              <TextField
                label="Event name"
                placeholder="e.g. Friday Night Sessions"
                value={form.name}
                onChange={set('name')}
                error={!!errors.name}
                helperText={errors.name}
                fullWidth
                required
                sx={fieldSx}
              />

              <VenueAutocomplete
                venueName={form.venueName}
                location={form.location}
                onVenueChange={(name, loc) => {
                  setForm((p) => ({ ...p, venueName: name, location: loc }));
                  setErrors((p) => ({ ...p, venueName: undefined }));
                }}
                fieldSx={fieldSx}
                error={errors.venueName}
              />

              <TextField
                label="Description"
                placeholder="What's the vibe tonight?"
                value={form.description}
                onChange={set('description')}
                fullWidth
                multiline
                rows={3}
                sx={fieldSx}
              />
            </Stack>

            <Divider sx={{ borderColor: alpha(theme.palette.common.white, 0.08) }} />

            {/* Schedule */}
            <Stack spacing={2}>
              <Stack direction="row" alignItems="center" justifyContent="space-between">
                <SectionLabel icon={iconPaths.calendar}>Schedule</SectionLabel>
                <Typography sx={{ fontSize: 12, fontWeight: 700, color: alpha(theme.palette.common.white, 0.38) }}>
                  Local time
                </Typography>
              </Stack>

              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 1.25 }}>
                {recurrenceOptions.map((option) => {
                  const active = form.recurrence === option.value;
                  return (
                    <Box
                      component="button"
                      key={option.value}
                      type="button"
                      onClick={() => setForm((p) => ({ ...p, recurrence: option.value, recurrenceDays: [], eventDate: '' }))}
                      sx={{
                        appearance: 'none',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'flex-start',
                        gap: 1.25,
                        minHeight: 78,
                        p: 1.5,
                        borderRadius: 1.5,
                        cursor: 'pointer',
                        color: active ? 'common.white' : alpha(theme.palette.common.white, 0.58),
                        bgcolor: active ? alpha(theme.palette.secondary.main, 0.16) : alpha(theme.palette.common.white, 0.045),
                        border: `1px solid ${active ? alpha(theme.palette.secondary.light, 0.45) : alpha(theme.palette.common.white, 0.11)}`,
                        boxShadow: active ? `0 14px 34px ${alpha(theme.palette.secondary.main, 0.14)}` : 'none',
                        transition: 'border-color 0.15s, background-color 0.15s, box-shadow 0.15s',
                        '&:hover': {
                          bgcolor: active ? alpha(theme.palette.secondary.main, 0.2) : alpha(theme.palette.common.white, 0.075),
                          borderColor: active ? alpha(theme.palette.secondary.light, 0.55) : alpha(theme.palette.common.white, 0.2),
                        },
                      }}
                    >
                      <Box
                        sx={{
                          width: 38,
                          height: 38,
                          borderRadius: '34% 46% 36% 44%',
                          transform: 'rotate(-4deg)',
                          display: 'grid',
                          placeItems: 'center',
                          flex: '0 0 auto',
                          color: active ? 'secondary.light' : alpha(theme.palette.common.white, 0.36),
                          bgcolor: active ? alpha(theme.palette.secondary.main, 0.18) : alpha(theme.palette.common.white, 0.06),
                          border: `1px solid ${active ? alpha(theme.palette.secondary.light, 0.28) : alpha(theme.palette.common.white, 0.08)}`,
                        }}
                      >
                        <AppIcon path={option.icon} sx={{ fontSize: 18 }} />
                      </Box>
                      <Box sx={{ minWidth: 0, textAlign: 'left' }}>
                        <Typography sx={{ fontSize: 14, fontWeight: 900, lineHeight: 1.2 }}>
                          {option.title}
                        </Typography>
                        <Typography sx={{ mt: 0.35, fontSize: 12, color: alpha(theme.palette.common.white, active ? 0.58 : 0.38), lineHeight: 1.35 }}>
                          {option.copy}
                        </Typography>
                      </Box>
                    </Box>
                  );
                })}
              </Box>

              {form.recurrence === 'once' ? (
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr 1fr' },
                    gap: 1.5,
                    p: { xs: 1.25, sm: 1.5 },
                    borderRadius: 1.5,
                    bgcolor: alpha(theme.palette.common.white, 0.035),
                    border: `1px solid ${alpha(theme.palette.common.white, 0.08)}`,
                  }}
                >
                  <MKDate
                    label="Date"
                    value={form.eventDate}
                    onChange={(v) => { setForm((p) => ({ ...p, eventDate: v })); setErrors((p) => ({ ...p, eventDate: undefined })); }}
                    fullWidth
                    required
                    futureOnly
                    error={!!errors.eventDate}
                    helperText={errors.eventDate}
                    sx={fieldSx}
                  />
                  <TextField
                    label="Start time"
                    type="time"
                    value={form.startTime}
                    onChange={set('startTime')}
                    fullWidth
                    required
                    InputLabelProps={{ shrink: true }}
                    error={!!errors.startTime}
                    helperText={errors.startTime}
                    sx={fieldSx}
                  />
                  <TextField
                    label="End time"
                    type="time"
                    value={form.endTime}
                    onChange={set('endTime')}
                    fullWidth
                    required
                    InputLabelProps={{ shrink: true }}
                    error={!!errors.endTime}
                    helperText={errors.endTime}
                    sx={fieldSx}
                  />
                </Box>
              ) : (
                <Stack spacing={2}>
                  {/* Day picker */}
                  <Stack
                    spacing={1.25}
                    sx={{
                      p: { xs: 1.25, sm: 1.5 },
                      borderRadius: 1.5,
                      bgcolor: alpha(theme.palette.common.white, 0.035),
                      border: `1px solid ${errors.recurrenceDays ? alpha(theme.palette.error.light, 0.45) : alpha(theme.palette.common.white, 0.08)}`,
                    }}
                  >
                    <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2}>
                      <Typography sx={{ fontSize: 13, fontWeight: 900, color: 'common.white' }}>
                        Repeat on
                      </Typography>
                      <Typography sx={{ fontSize: 12, fontWeight: 700, color: errors.recurrenceDays ? 'error.light' : alpha(theme.palette.common.white, 0.42) }}>
                        {errors.recurrenceDays ?? selectedDayText}
                      </Typography>
                    </Stack>

                    <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, minmax(0, 1fr))', gap: { xs: 0.6, sm: 0.75 } }}>
                      {WEEK_DAYS.map(({ label, short, dow }) => {
                        const selected = form.recurrenceDays.includes(dow);
                        return (
                          <Box
                            component="button"
                            key={dow}
                            type="button"
                            onClick={() => toggleDay(dow)}
                            title={label}
                            sx={{
                              appearance: 'none',
                              p: 0,
                              minWidth: 0,
                              height: { xs: 48, sm: 54 },
                              borderRadius: 1.25,
                              display: 'grid',
                              placeItems: 'center',
                              cursor: 'pointer',
                              fontSize: { xs: 11, sm: 12 },
                              fontWeight: 900,
                              color: selected ? 'common.white' : alpha(theme.palette.common.white, 0.42),
                              bgcolor: selected ? alpha(theme.palette.secondary.main, 0.24) : alpha(theme.palette.common.white, 0.045),
                              border: `1px solid ${selected ? alpha(theme.palette.secondary.light, 0.5) : alpha(theme.palette.common.white, 0.1)}`,
                              boxShadow: selected ? `inset 0 -2px 0 ${theme.palette.secondary.light}` : 'none',
                              transition: 'border-color 0.15s, background-color 0.15s, color 0.15s',
                              '&:hover': {
                                bgcolor: selected ? alpha(theme.palette.secondary.main, 0.32) : alpha(theme.palette.common.white, 0.08),
                                borderColor: selected ? alpha(theme.palette.secondary.light, 0.6) : alpha(theme.palette.common.white, 0.2),
                                color: 'common.white',
                              },
                            }}
                          >
                            {short}
                          </Box>
                        );
                      })}
                    </Box>
                  </Stack>

                  {/* Times + optional end date */}
                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr 1fr' },
                      gap: 1.5,
                      p: { xs: 1.25, sm: 1.5 },
                      borderRadius: 1.5,
                      bgcolor: alpha(theme.palette.common.white, 0.035),
                      border: `1px solid ${alpha(theme.palette.common.white, 0.08)}`,
                    }}
                  >
                    <TextField
                      label="Start time"
                      type="time"
                      value={form.startTime}
                      onChange={set('startTime')}
                      fullWidth
                      required
                      InputLabelProps={{ shrink: true }}
                      error={!!errors.startTime}
                      helperText={errors.startTime}
                      sx={fieldSx}
                    />
                    <TextField
                      label="End time"
                      type="time"
                      value={form.endTime}
                      onChange={set('endTime')}
                      fullWidth
                      required
                      InputLabelProps={{ shrink: true }}
                      error={!!errors.endTime}
                      helperText={errors.endTime}
                      sx={fieldSx}
                    />
                    <MKDate
                      label="Ends on (optional)"
                      value={form.recurrenceEndDate}
                      onChange={(v) => { setForm((p) => ({ ...p, recurrenceEndDate: v })); setErrors((p) => ({ ...p, recurrenceEndDate: undefined })); }}
                      fullWidth
                      futureOnly
                      error={!!errors.recurrenceEndDate}
                      helperText={errors.recurrenceEndDate}
                      sx={fieldSx}
                    />
                  </Box>
                </Stack>
              )}
            </Stack>

            <Divider sx={{ borderColor: alpha(theme.palette.common.white, 0.08) }} />

            {/* Spotify playlist */}
            <Stack spacing={2}>
              <Stack direction="row" alignItems="center" justifyContent="space-between">
                <SectionLabel icon={iconPaths.spotify}>Spotify Playlist</SectionLabel>
                {form.spotifyPlaylistId && (
                  <Button
                    size="small"
                    onClick={() => setForm((p) => ({ ...p, spotifyPlaylistId: undefined, spotifyPlaylistName: undefined }))}
                    sx={{ fontSize: 11, color: alpha(theme.palette.common.white, 0.4), minWidth: 0, '&:hover': { color: 'common.white' } }}
                  >
                    Clear
                  </Button>
                )}
              </Stack>

              {errors.spotifyPlaylistId && (
                <Typography sx={{ fontSize: 12, color: 'error.light' }}>{errors.spotifyPlaylistId}</Typography>
              )}

              {!user?.spotifyConnected ? (
                <Typography sx={{ fontSize: 13, color: alpha(theme.palette.common.white, 0.4), fontStyle: 'italic' }}>
                  Connect Spotify on the{' '}
                  <Box component="span" onClick={() => navigate(routePaths.djSpotify)} sx={{ color: SPOTIFY_GREEN, cursor: 'pointer', textDecoration: 'underline' }}>
                    Spotify page
                  </Box>
                  {' '}to link a playlist to this event.
                </Typography>
              ) : loadingPlaylists ? (
                <Stack direction="row" spacing={1.5} sx={{ overflow: 'hidden' }}>
                  {[...Array(4)].map((_, i) => (
                    <Skeleton key={i} variant="rounded" width={120} height={44} sx={{ flexShrink: 0, borderRadius: 1, bgcolor: alpha('#fff', 0.06) }} />
                  ))}
                </Stack>
              ) : playlists.length === 0 ? (
                <Typography sx={{ fontSize: 13, color: alpha(theme.palette.common.white, 0.4) }}>
                  No playlists found on your Spotify account.
                </Typography>
              ) : (
                <Box sx={{ display: 'flex', gap: 1.25, flexWrap: 'wrap' }}>
                  {playlists.map((pl, i) => {
                    const selected = form.spotifyPlaylistId === pl.playlistId;
                    return (
                      <Box
                        key={pl.playlistId}
                        onClick={() => setForm((p) => ({
                          ...p,
                          spotifyPlaylistId: selected ? undefined : pl.playlistId,
                          spotifyPlaylistName: selected ? undefined : pl.name,
                        }))}
                        sx={{
                          display: 'flex', alignItems: 'center', gap: 1,
                          px: 1.5, py: 1,
                          borderRadius: 1.5,
                          border: `1px solid ${selected ? SPOTIFY_GREEN : alpha(theme.palette.common.white, 0.12)}`,
                          bgcolor: selected ? alpha(SPOTIFY_GREEN, 0.12) : alpha(theme.palette.common.white, 0.05),
                          cursor: 'pointer',
                          transition: 'all 0.15s',
                          '&:hover': { bgcolor: selected ? alpha(SPOTIFY_GREEN, 0.18) : alpha(theme.palette.common.white, 0.1) },
                        }}
                      >
                        {pl.imageUrl ? (
                          <Box component="img" src={pl.imageUrl} sx={{ width: 30, height: 30, borderRadius: 1, objectFit: 'cover', flexShrink: 0 }} />
                        ) : (
                          <Box sx={{ width: 30, height: 30, borderRadius: 1, background: PLAYLIST_GRADIENTS[i % PLAYLIST_GRADIENTS.length], display: 'grid', placeItems: 'center', flexShrink: 0 }}>
                            <AppIcon path={iconPaths.spotify} sx={{ fontSize: 14, color: '#fff' }} />
                          </Box>
                        )}
                        <Box sx={{ minWidth: 0 }}>
                          <Typography noWrap sx={{ fontSize: 13, fontWeight: 700, color: selected ? SPOTIFY_GREEN : 'common.white', maxWidth: 140 }}>
                            {pl.name}
                          </Typography>
                          <Typography sx={{ fontSize: 11, color: alpha(theme.palette.common.white, 0.4) }}>
                            {pl.trackCount} tracks
                          </Typography>
                        </Box>
                        {selected && <Chip label="Selected" size="small" sx={{ height: 18, fontSize: 10, fontWeight: 800, color: SPOTIFY_GREEN, bgcolor: alpha(SPOTIFY_GREEN, 0.15), border: `1px solid ${alpha(SPOTIFY_GREEN, 0.3)}`, '& .MuiChip-label': { px: 0.75 }, ml: 0.5 }} />}
                      </Box>
                    );
                  })}
                </Box>
              )}

              {/* Share playlist toggle — only shown when a playlist is selected */}
              {form.spotifyPlaylistId && (
                <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ pt: 0.5 }}>
                  <Box>
                    <Typography sx={{ fontSize: 13, fontWeight: 700, color: 'common.white' }}>
                      Share playlist with guests
                    </Typography>
                    <Typography sx={{ fontSize: 12, color: alpha(theme.palette.common.white, 0.4), mt: 0.25 }}>
                      Guests can see the linked playlist on the event page
                    </Typography>
                  </Box>
                  <Stack direction="row" spacing={0.5} sx={{ p: 0.375, borderRadius: 99, border: `1px solid ${alpha(theme.palette.common.white, 0.12)}`, bgcolor: alpha(theme.palette.common.white, 0.06), flexShrink: 0, ml: 2 }}>
                    {([false, true] as const).map((val) => {
                      const active = form.sharePlaylist === val;
                      return (
                        <Button
                          key={String(val)}
                          size="small"
                          onClick={() => setForm((p) => ({ ...p, sharePlaylist: val }))}
                          sx={{
                            px: 1.5, py: 0.5, minWidth: 0, fontSize: 12, fontWeight: active ? 900 : 600,
                            color: active ? (val ? SPOTIFY_GREEN : 'common.white') : alpha(theme.palette.common.white, 0.4),
                            bgcolor: active ? alpha(val ? SPOTIFY_GREEN : theme.palette.common.white, 0.12) : 'transparent',
                            borderRadius: 99,
                            boxShadow: active ? `inset 0 0 0 1px ${alpha(val ? SPOTIFY_GREEN : theme.palette.common.white, 0.2)}` : 'none',
                            '&:hover': { color: val ? SPOTIFY_GREEN : 'common.white', bgcolor: alpha(val ? SPOTIFY_GREEN : theme.palette.common.white, 0.08) },
                          }}
                        >
                          {val ? 'Yes' : 'No'}
                        </Button>
                      );
                    })}
                  </Stack>
                </Stack>
              )}

            </Stack>

          </Stack>
        </Box>

        {/* Actions */}
        <Stack direction="row" justifyContent="flex-end" spacing={1.5}>
          <Button
            onClick={() => navigate(routePaths.djEvents)}
            disabled={submitting}
            sx={{
              color: alpha(theme.palette.common.white, 0.55),
              '&:hover': { color: 'common.white', bgcolor: alpha(theme.palette.common.white, 0.08) },
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={submitting}
            endIcon={submitting ? <CircularProgress size={16} sx={{ color: 'inherit' }} /> : <AppIcon path={iconPaths.arrow} />}
            sx={{
              minHeight: 44,
              px: 3,
              bgcolor: 'secondary.main',
              boxShadow: `0 10px 28px ${alpha(theme.palette.secondary.main, 0.36)}`,
              '&:hover': { bgcolor: 'secondary.dark' },
              '&.Mui-disabled': { bgcolor: alpha(theme.palette.secondary.main, 0.4) },
            }}
          >
            {submitting ? 'Creating…' : 'Create Event'}
          </Button>
        </Stack>

      </Stack>
    </DjPageShell>
  );
}
